import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, isProvince, isWard, isXaphuongTinhThanh, notify } from '~/utils/helper';
import { IEditUser, IUser } from '~/utils/interface/user';
import userValidation from '~/validation/user';
import { IMasterDataItem } from '~/utils/interface/master';
import powerGroupApi from '~/apis/power-group';
import i18n from '~/utils/i18n';
import { LanguageEnum, RoleIdUserEnum } from '~/utils/enum';
import locationApi from '~/apis/location';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import userApi from '~/apis/user';
import { useAppDispatch } from '~/redux/hooks';
import { handleCreateUser } from '~/thunks/user/userThunk';

type EditUserDialogProps = {
  open: boolean;
  data?: IUser;
  mode: string;
  currentUser?: IUser;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IEditUser = {
  name: '',
  username: '',
  email: '',
  phone: '',
  address: '',
  ward_id: '',
  province_id: '',
  roleid: undefined,
};

const EditUserDialog = (props: EditUserDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/user');

  const [powerGroups, setPowerGroups] = useState<IMasterDataItem[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);

  const form = useFormik({
    validateOnChange: false,
    validationSchema: userValidation(t).edit,
    initialValues,
    onSubmit: async (values) => {
      if (values.roleid === RoleIdUserEnum.TRUNG_UONG) {
        values.groupid = 1;
      } else {
        values.groupid = values.roleid;
      }

      if (props.mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateUser(values)).unwrap();
          notify('success', t('message.success.create'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }

      if (props.mode === DIALOG_MODE_EDIT && values?.username) {
        try {
          await userApi.update(values);
          handleSetOpen(false, true);
          notify('success', t('message.success.edit', { username: values.username }));
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const response = await powerGroupApi.getList({ username: props.currentUser?.username });
        setPowerGroups(
          response.data.result?.map((item) => {
            return {
              id: cast(item.id),
              name: `${i18n.language === LanguageEnum.VI ? item.namevi : item.nameen}`,
              data0: item.nameen,
              data1: item.namevi,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, [props.currentUser?.username]);

  useEffect(() => {
    getListProvinces();
  }, [props.open]);

  useEffect(() => {
    if (form.values.province_id) {
      (async () => {
        try {
          const response = await locationApi.getListWards({ province: form.values.province_id ?? '' });
          setWardOptions(
            response.data.map((item) => {
              return {
                id: item.code,
                name: item.name,
                data0: item.name,
                data1: item.name_with_type,
                data2: item.type,
                data3: item.parent_code,
              };
            })
          );
        } catch (error) {
          setWardOptions([]);
          handleApiError(error);
        }
      })();
    } else setWardOptions([]);
  }, [form.values.province_id]);

  useEffect(() => {
    if (isXaphuongTinhThanh(cast(props.currentUser?.roleid)))
      form.setFieldValue('province_id', String(props?.currentUser?.province_id));
    form.setFieldValue('ward_id', String(props?.currentUser?.ward_id || ''));

    if (props.open && props.data && props.mode === DIALOG_MODE_EDIT) {
      form.setValues({
        username: props.data.username,
        name: props.data.name,
        phone: props.data.phone,
        email: props.data.email,
        roleid: props.data.roleid,
        groupid: props.data.groupid,
        province_id: props.data.province_id,
        ward_id: props.data.ward_id,
        address: props.data.address,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data, props.mode]);

  const getListProvinces = async () => {
    try {
      const response = await locationApi.getListProvinces();
      setProvinceOptions(
        response.data.map((item) => {
          return {
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
          };
        })
      );
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle className='bg-white text-xl font-semibold text-[#161A1F]'>
        {props.mode === DIALOG_MODE_EDIT ? t('edit') : t('create')}
        <IconButton className='p-1' onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component='form'>
          <BaseRow className='mb-2 gap-3'>
            <Box className='flex-1'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('username/cccd')}</InputLabel>
              <TextField
                autoComplete='off'
                className='w-full rounded-xl'
                name='username'
                placeholder={
                  form.values.roleid === RoleIdUserEnum.NGUOI_DEO ? t('placeholder.cccd') : t('placeholder.role_name')
                }
                value={form.values?.username ?? ''}
                onChange={form.handleChange}
                disabled={props.mode === DIALOG_MODE_EDIT}
                error={Boolean(form.errors?.username)}
                FormHelperTextProps={{
                  style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
                }}
              />
              {form.errors?.username && (
                <Box>
                  <FormHelperText error={true} className='text-xs'>
                    {form.values.roleid === RoleIdUserEnum.NGUOI_DEO ? t('validation.cccd') : form.errors?.username}
                  </FormHelperText>
                </Box>
              )}
            </Box>
            <Box className='flex-1'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('name')}</InputLabel>
              <TextField
                autoComplete='off'
                className='w-full rounded-xl'
                name='name'
                placeholder={t('placeholder.name')}
                value={form.values?.name ?? ''}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
                FormHelperTextProps={{
                  style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
                }}
              />
            </Box>
          </BaseRow>
          <BaseRow className='mb-2 gap-3'>
            <Box className='flex-1'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('phone')}</InputLabel>
              <TextField
                autoComplete='off'
                className='w-full rounded-xl'
                name='phone'
                placeholder={t('placeholder.phone')}
                value={form.values?.phone ?? ''}
                onChange={form.handleChange}
                error={Boolean(form.errors?.phone)}
                helperText={form.errors?.phone}
                FormHelperTextProps={{
                  style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
                }}
              />
            </Box>
            <Box className='flex-1'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('email')}</InputLabel>
              <TextField
                autoComplete='off'
                className='w-full rounded-xl'
                name='email'
                placeholder={t('placeholder.email')}
                value={form.values?.email ?? ''}
                onChange={form.handleChange}
                error={Boolean(form.errors?.email)}
                helperText={form.errors?.email}
                FormHelperTextProps={{
                  style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
                }}
              />
            </Box>
          </BaseRow>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('address')}</InputLabel>
            <TextField
              autoComplete='off'
              className='w-full rounded-xl'
              name='address'
              placeholder={t('placeholder.address')}
              value={form.values?.address ?? ''}
              onChange={form.handleChange}
              error={Boolean(form.errors?.address)}
              helperText={form.errors?.address}
              FormHelperTextProps={{
                style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
              }}
            />
          </Box>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm'>{t('roleid.title')}</InputLabel>
            <FormControl className='w-full justify-between'>
              <RadioGroup
                row
                className='w-full justify-between'
                name='roleid'
                value={Number(form.values.roleid)}
                onChange={(e) => {
                  const roleId = Number(e.target.value);
                  form.setFieldValue('roleid', roleId);

                  if (isProvince(cast(props.currentUser?.roleid))) {
                    form.setFieldValue('province_id', String(props.currentUser?.province_id || ''));
                  } else if (isWard(cast(props.currentUser?.roleid))) {
                    form.setFieldValue('ward_id', String(props.currentUser?.ward_id || ''));
                  } else {
                    form.setFieldValue('province_id', '');
                    form.setFieldValue('ward_id', '');
                  }
                }}
              >
                {powerGroups?.map((item) => {
                  return (
                    <FormControlLabel
                      key={item.id}
                      value={item.id}
                      control={
                        <Radio
                          size='medium'
                          sx={{
                            color: '#7C8796',
                            '&.Mui-checked': {
                              color: '#2E89C8',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 24,
                            },
                          }}
                        />
                      }
                      label={cast(item.name)}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: 16,
                          color: '#161A1F',
                        },
                      }}
                    />
                  );
                })}
              </RadioGroup>
              {form.touched.roleid && form.errors.roleid && (
                <FormHelperText sx={{ fontSize: '12px', lineHeight: '16px', margin: 0, color: '#ed1002' }}>
                  {form.errors.roleid}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          {[
            RoleIdUserEnum.NGUOI_DEO,
            RoleIdUserEnum.TINH_THANH,
            RoleIdUserEnum.QUAN_TRI,
            RoleIdUserEnum.XA_PHUONG,
          ].includes(Number(form.values.roleid)) && (
            <>
              <Box className={`${Number(form.values.roleid) === RoleIdUserEnum.TINH_THANH ? 'mb-4' : 'mb-2'}`}>
                <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('province_id')}</InputLabel>
                <BaseSelect
                  options={
                    isXaphuongTinhThanh(cast(props.currentUser?.roleid))
                      ? provinceOptions.filter(
                          (item: IMasterDataItem) => item.id === String(props.currentUser?.province_id)
                        )
                      : provinceOptions
                  }
                  value={String(form.values.province_id)}
                  placeholder={t('placeholder.province_id')}
                  name='province_id'
                  isSearchEnable
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.province_id)}
                  helperText={form.errors?.province_id}
                  disabled={isXaphuongTinhThanh(cast(props.currentUser?.roleid))}
                />
              </Box>
              {Number(form.values.roleid) !== RoleIdUserEnum.TINH_THANH && (
                <Box className='mb-4'>
                  <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('ward_id')}</InputLabel>
                  <BaseSelect
                    options={wardOptions}
                    value={String(form.values.ward_id)}
                    placeholder={t('placeholder.ward_id')}
                    name='ward_id'
                    isSearchEnable
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.ward_id)}
                    helperText={form.errors?.ward_id}
                    disabled={isWard(cast(props.currentUser?.roleid))}
                  />
                </Box>
              )}
            </>
          )}
          <Box className='flex justify-between gap-3'>
            <Button
              variant='outlined'
              className='w-full bg-[#D7DEE7] border-0 !border-inherit text-[#404854] text-base font-semibold'
              onClick={() => handleSetOpen(false)}
            >
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            <Button
              variant='contained'
              className='w-full bg-[#123B6D] text-base font-semibold'
              onClick={() => form.handleSubmit()}
            >
              {props.mode === DIALOG_MODE_CREATE
                ? t('create', { ns: 'admin/common' })
                : t('saveChange', { ns: 'admin/common' })}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(EditUserDialog);
